import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, CheckCircle2, AlertCircle, Loader2, Plane } from "lucide-react";
import { GUIDES } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;

export default function GuideDownload() {
  const [phase, setPhase] = useState("checking"); // checking | ready | error
  const [info, setInfo] = useState(null); // { guideTitle, token }
  const [errorMsg, setErrorMsg] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const token = params.get("token");

    const fail = (msg) => {
      setErrorMsg(msg);
      setPhase("error");
    };

    const pollStatus = async () => {
      if (polls.current >= MAX_POLLS) {
        fail("We couldn't confirm your payment in time. If you were charged, your download link is on its way by email.");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await axios.get(`${API}/guides/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" && data.token) {
          setInfo({ guideTitle: data.guide_title, token: data.token });
          setPhase("ready");
        } else if (data.status === "expired") {
          fail("Your checkout session expired before payment was completed. No charge was made — head back and try again.");
        } else {
          setTimeout(pollStatus, 2000);
        }
      } catch {
        fail("Something went wrong while confirming your payment.");
      }
    };

    const validateToken = async () => {
      try {
        const { data } = await axios.get(`${API}/guides/download/${token}`);
        setInfo({ guideTitle: data.guide_title, token });
        setPhase("ready");
      } catch (err) {
        const status = err.response?.status;
        fail(
          status === 410
            ? "This download link has expired (links last 48 hours)."
            : "This download link isn't valid."
        );
      }
    };

    if (sessionId) pollStatus();
    else if (token) validateToken();
    else fail("This page needs a valid download link.");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, reads params from URL
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col" data-testid={GUIDES.downloadPage}>
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-lg bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl">
          {phase === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6">Confirming your payment…</h1>
              <p className="text-[var(--ocean)] mt-2 text-sm">Hang tight — this only takes a few seconds.</p>
            </>
          )}

          {phase === "ready" && (
            <>
              <CheckCircle2 size={44} className="mx-auto text-[var(--lime-deep)]" />
              <span className="num-tag text-xs block mt-6">Your guide is ready</span>
              <h1 className="font-display text-3xl font-extrabold mt-2 leading-tight">{info.guideTitle}</h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
                Cheers for grabbing a guide! Hit the button below to download your PDF.
                We've also emailed you a backup link.
              </p>
              <a
                data-testid={GUIDES.downloadBtn}
                href={`${API}/guides/download/${info.token}/file`}
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-8 py-3.5 hover:bg-[var(--ink-soft)] transition-colors"
              >
                <Download size={18} /> Download your guide (PDF)
              </a>
              <p className="text-xs text-[var(--ocean)] mt-5">
                This link works for 48 hours. Need it again later?{" "}
                <a href="mailto:hello@theditchcollective.co.nz" className="underline">
                  hello@theditchcollective.co.nz
                </a>
              </p>
            </>
          )}

          {phase === "error" && (
            <>
              <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6" data-testid={GUIDES.error}>
                Hmm, that didn't work
              </h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
              <p className="text-sm text-[var(--ink)] mt-5">
                Flick us an email at{" "}
                <a href="mailto:hello@theditchcollective.co.nz" className="font-bold underline">
                  hello@theditchcollective.co.nz
                </a>{" "}
                and we'll sort you out.
              </p>
              <a href="/" className="mt-6 inline-block btn-ghost text-sm">
                ← Back to the site
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
